import { apiClient } from '@/services/api';

// Initialize hiddenJobs from localStorage when store is created
let initialHiddenJobs = [];
try {
  // Only load from localStorage if the user is logged in
  const isLoggedIn = !!localStorage.getItem('token');

  if (isLoggedIn) {
    const hiddenJobsStr = localStorage.getItem('hiddenJobs');
    if (hiddenJobsStr) {
      const parsed = JSON.parse(hiddenJobsStr);
      if (Array.isArray(parsed)) {
        initialHiddenJobs = parsed;
      }
    }
  } else {
    // Clear hidden jobs from localStorage if not logged in
    localStorage.removeItem('hiddenJobs');
  }
} catch (error) {
  console.error('Error initializing hidden jobs from localStorage:', error);
}

const state = {
  hiddenJobs: initialHiddenJobs,
  loading: false,
  error: null
};

const mutations = {
  SET_HIDDEN_JOBS(state, jobs) {
    state.hiddenJobs = Array.isArray(jobs) ? jobs : [];
  },
  ADD_HIDDEN_JOB(state, job) {
    // Ensure hiddenJobs is an array
    if (!Array.isArray(state.hiddenJobs)) {
      state.hiddenJobs = [];
    }

    // Check if this job already exists in the hidden jobs
    // We need to check multiple possible identifiers because jobs can be hidden in different formats

    // Check direct ID match
    const existsByDirectId = state.hiddenJobs.some(hiddenJob => hiddenJob.id === job.id);

    // Check job ID match via job.job property
    const existsByJobProperty = state.hiddenJobs.some(hiddenJob =>
      hiddenJob.job === job.job ||
      hiddenJob.job === job.id ||
      (job.job && hiddenJob.job === job.job)
    );

    // Check job details ID match
    const existsByJobDetails = state.hiddenJobs.some(hiddenJob =>
      (hiddenJob.job_details && job.job_details && hiddenJob.job_details.id === job.job_details.id) ||
      (hiddenJob.job_details && hiddenJob.job_details.id === job.id) ||
      (job.job_details && job.job_details.id && hiddenJob.job === job.job_details.id)
    );

    // Check if any of the existence checks passed
    const exists = existsByDirectId || existsByJobProperty || existsByJobDetails;

    if (!exists) {
      state.hiddenJobs.push(job);
    }
  },
  REMOVE_HIDDEN_JOB(state, jobId) {
    // Ensure hiddenJobs is an array
    if (!Array.isArray(state.hiddenJobs)) {
      state.hiddenJobs = [];
      return;
    }

    // First try to remove by direct ID match
    let filteredJobs = state.hiddenJobs.filter(job => job.id !== jobId);

    // If no jobs were removed, try to remove by job_details.id match
    if (filteredJobs.length === state.hiddenJobs.length) {
      filteredJobs = state.hiddenJobs.filter(job =>
        !(job.job_details && job.job_details.id === jobId)
      );
    }

    // If still no jobs were removed, try to remove by job_id match
    if (filteredJobs.length === state.hiddenJobs.length) {
      filteredJobs = state.hiddenJobs.filter(job => job.job_id !== jobId);
    }

    state.hiddenJobs = filteredJobs;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  }
};

const actions = {
  // Fetch hidden jobs from API and localStorage
  async fetchHiddenJobs({ commit }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    // First check if user is logged in
    const isLoggedIn = !!localStorage.getItem('token');
    if (!isLoggedIn) {
      // If not logged in, clear hidden jobs and return
      commit('SET_HIDDEN_JOBS', []);
      commit('SET_LOADING', false);
      return;
    }

    try {
      // First try to get from API if user is authenticated
      try {
        const response = await apiClient.get('/hidden-jobs/');

        // Make sure we're working with an array
        let hiddenJobs = [];
        if (Array.isArray(response.data)) {
          hiddenJobs = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          hiddenJobs = response.data.results;
        } else {
          hiddenJobs = [];
        }

        // Add identifiers for easy lookup
        hiddenJobs = hiddenJobs.map(job => {
          if (job.job_details) {
            // Make sure we can find these jobs by their job_details.id
            return {
              ...job,
              job_id: job.job_details.id
            };
          }
          return job;
        });

        commit('SET_HIDDEN_JOBS', hiddenJobs);
        // Update localStorage with the latest data from API
        localStorage.setItem('hiddenJobs', JSON.stringify(hiddenJobs));
      } catch (apiError) {
        console.error('API Error fetching hidden jobs:', apiError);
        if (apiError.response) {
          console.error('API Error details:', apiError.response.data);
        }

        // Fallback to localStorage if API fails
        const hiddenJobsData = localStorage.getItem('hiddenJobs');
        if (hiddenJobsData) {
          try {
            const parsedData = JSON.parse(hiddenJobsData);
            if (Array.isArray(parsedData)) {
              commit('SET_HIDDEN_JOBS', parsedData);
            } else {
              console.error('Hidden jobs data is not an array, resetting to empty array');
              commit('SET_HIDDEN_JOBS', []);
              // Clean up corrupted data
              localStorage.setItem('hiddenJobs', JSON.stringify([]));
            }
          } catch (parseError) {
            console.error('Error parsing hidden jobs data:', parseError);
            commit('SET_HIDDEN_JOBS', []);
            // Clean up corrupted data
            localStorage.setItem('hiddenJobs', JSON.stringify([]));
          }
        } else {
          commit('SET_HIDDEN_JOBS', []);
        }
      }
    } catch (error) {
      console.error('Error fetching hidden jobs:', error);
      commit('SET_ERROR', 'Failed to load hidden jobs. Please try again.');
      commit('SET_HIDDEN_JOBS', []);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Hide job via API and localStorage
  async hideJob({ commit, state }, job) {
    // First check if user is logged in
    const isLoggedIn = !!localStorage.getItem('token');
    if (!isLoggedIn) {
      commit('SET_ERROR', 'Authentication required');
      return false;
    }

    try {
      let hiddenJobData = null;

      // Try to hide via API first
      try {
        const response = await apiClient.post('/hidden-jobs/', { job_id: job.id });

        // Use the response data from the API, which should contain the hidden job object
        hiddenJobData = response.data;

        // If response doesn't contain job_details but we have the job object,
        // combine them for better display in the UI
        if (hiddenJobData && !hiddenJobData.job_details && job) {
          hiddenJobData = {
            ...hiddenJobData,
            job_details: job,
            job_id: job.id // Add job_id for easier lookup
          };
        }
      } catch (apiError) {
        console.error('API Error when hiding job:', apiError);
        if (apiError.response) {
          console.error('API Error details:', apiError.response.data);

          // If job is already hidden, we can continue
          if (!(apiError.response.status === 200 &&
              apiError.response.data &&
              apiError.response.data.detail === "Job already hidden")) {
            throw apiError;
          }
        } else {
          throw apiError;
        }
      }

      // If we have hidden job data from the API, use it
      if (hiddenJobData) {
        commit('ADD_HIDDEN_JOB', hiddenJobData);
      } else {
        // Otherwise use the job object that was passed in

        // Create a hidden job object similar to what would be returned by the API
        const localHiddenJob = {
          id: `local_${Date.now()}`, // Temporary ID until we fetch from API
          job: job.id,
          job_details: job,
          job_id: job.id,
          date_hidden: new Date().toISOString()
        };

        commit('ADD_HIDDEN_JOB', localHiddenJob);
      }

      // Save to localStorage as backup
      if (Array.isArray(state.hiddenJobs)) {
        localStorage.setItem('hiddenJobs', JSON.stringify(state.hiddenJobs));
      } else {
        // If somehow hiddenJobs is not an array, initialize it
        commit('SET_HIDDEN_JOBS', hiddenJobData ? [hiddenJobData] : [job]);
        localStorage.setItem('hiddenJobs', JSON.stringify(hiddenJobData ? [hiddenJobData] : [job]));
      }

      return { success: true, data: hiddenJobData };
    } catch (error) {
      console.error('Error hiding job:', error);
      commit('SET_ERROR', 'Failed to hide job. Please try again.');
      return { success: false, error };
    }
  },

  // Remove job from hidden jobs (unhide)
  async unhideJob({ commit, state }, jobId) {
    // First check if user is logged in
    const isLoggedIn = !!localStorage.getItem('token');
    if (!isLoggedIn) {
      commit('SET_ERROR', 'Authentication required');
      return { success: false };
    }

    try {
      // First figure out the actual hidden job record ID
      let actualJobId = jobId;

      // If the job is hidden with a different ID (e.g. the hidden job record ID versus the job ID)
      // we need to find the correct hidden job record to delete
      if (Array.isArray(state.hiddenJobs)) {
        // Look for matching job_details.id
        const jobWithMatchingDetails = state.hiddenJobs.find(
          hiddenJob => hiddenJob.job_details && hiddenJob.job_details.id === jobId
        );

        if (jobWithMatchingDetails) {
          actualJobId = jobWithMatchingDetails.id;
        } else {
          // Look for matching job ID in the job field
          const jobWithMatchingJob = state.hiddenJobs.find(
            hiddenJob => hiddenJob.job === jobId || hiddenJob.job_id === jobId
          );

          if (jobWithMatchingJob) {
            actualJobId = jobWithMatchingJob.id;
          }
        }
      }

      // Try to remove from API first
      try {
        await apiClient.delete(`/hidden-jobs/${actualJobId}/`);
      } catch (apiError) {
        console.error('API Error when unhiding job:', apiError);
        // If 404, it's already removed from the server, so we can just remove it locally
        if (apiError.response && apiError.response.status !== 404) {
          throw apiError;
        }
      }

      // Remove from local state
      commit('REMOVE_HIDDEN_JOB', jobId);

      // Update localStorage
      localStorage.setItem('hiddenJobs', JSON.stringify(state.hiddenJobs));

      return { success: true };
    } catch (error) {
      console.error('Error unhiding job:', error);
      commit('SET_ERROR', 'Failed to unhide job. Please try again.');
      return { success: false, error };
    }
  },

  // Check if a job is hidden
  isJobHidden({ state }, jobId) {
    // Convert jobId to number if it's a string
    const id = typeof jobId === 'string' ? parseInt(jobId, 10) : jobId;

    if (!Array.isArray(state.hiddenJobs)) {
      return false;
    }

    // First check direct job ID match
    const directMatch = state.hiddenJobs.some(job => job.id === id);
    if (directMatch) {
      return true;
    }

    // Then check if it's in job_details nested structure
    const nestedMatch = state.hiddenJobs.some(job =>
      job.job_details && job.job_details.id === id
    );

    return nestedMatch;
  },

  // Toggle job hidden state
  async toggleHideJob({ dispatch, getters }, job) {
    if (!job || !job.id) {
      console.error('Invalid job object provided to toggleHideJob');
      return { success: false };
    }

    // Check if already hidden
    const isAlreadyHidden = getters.isJobHidden(job.id);

    if (isAlreadyHidden) {
      // Remove from hidden jobs
      return await dispatch('unhideJob', job.id);
    } else {
      // Add to hidden jobs
      const result = await dispatch('hideJob', job);
      return result;
    }
  }
};

const getters = {
  hiddenJobs: state => Array.isArray(state.hiddenJobs) ? state.hiddenJobs : [],
  hiddenJobIds: state => {
    if (!Array.isArray(state.hiddenJobs)) return [];

    // Collect direct IDs and nested IDs
    const ids = [];
    state.hiddenJobs.forEach(job => {
      if (job.id) ids.push(job.id);
      // Check for nested job_details
      if (job.job_details && job.job_details.id) {
        ids.push(job.job_details.id);
      }
      // If we have a job_id instead (from API)
      if (job.job_id) ids.push(job.job_id);
    });

    return ids;
  },
  isJobHidden: state => jobId => {
    // Convert jobId to number if it's a string
    const id = typeof jobId === 'string' ? parseInt(jobId, 10) : jobId;

    if (!Array.isArray(state.hiddenJobs)) {
      return false;
    }

    // First check direct job ID match
    const directMatch = state.hiddenJobs.some(job => job.id === id);
    if (directMatch) {
      return true;
    }

    // Then check if it's in job_details nested structure
    const nestedMatch = state.hiddenJobs.some(job =>
      job.job_details && job.job_details.id === id
    );

    // Check for job_id property that might be used by API
    const apiMatch = state.hiddenJobs.some(job => job.job_id === id);

    return directMatch || nestedMatch || apiMatch;
  },
  loading: state => state.loading,
  error: state => state.error
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
